import { useEffect, useState, useCallback } from "react";
import { PAPER_SECTIONS } from "@/lib/sections";
import { cn } from "@/lib/cn";

/**
 * Sidebar for the strategy paper. Fifteen chapters, no act grouping —
 * the paper reads as one continuous briefing. Numbered so the reader
 * always knows their place. Scroll-spy marks the active chapter.
 */
export const Sidebar = () => {
  const [active, setActive] = useState("hero");

  useEffect(() => {
    const ids = PAPER_SECTIONS.map((s) => s.id);
    const targets = ids
      .map((id) => document.getElementById(id))
      .filter(Boolean);
    if (!targets.length) return;

    const obs = new IntersectionObserver(
      (entries) => {
        const inView = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (inView[0]) setActive(inView[0].target.id);
      },
      { rootMargin: "-15% 0px -70% 0px", threshold: [0, 0.1, 0.5, 1] }
    );

    targets.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);

  const jump = useCallback((id) => (e) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      window.history.replaceState(null, "", `#${id}`);
    }
  }, []);

  return (
    <aside
      data-testid="paper-sidebar"
      className="hidden w-72 shrink-0 border-r border-border bg-background lg:block"
    >
      <div className="sticky top-14 flex h-[calc(100vh-3.5rem)] flex-col overflow-y-auto px-6 py-10">
        <div className="mb-5 text-[10px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
          Contents
        </div>
        <nav aria-label="Table of contents" className="flex flex-col" data-testid="sidebar-nav">
          {PAPER_SECTIONS.map((s) => {
            const isActive = active === s.id;
            return (
              <a
                key={s.id}
                href={`#${s.id}`}
                onClick={jump(s.id)}
                data-testid={`sidebar-link-${s.id}`}
                className={cn(
                  "group flex items-baseline gap-4 rounded-md py-1.5 pr-2 text-[13px] leading-snug transition-colors",
                  isActive
                    ? "text-foreground font-medium"
                    : "text-muted-foreground hover:text-foreground"
                )}
              >
                <span className={cn(
                  "font-mono text-[10px] tabular-nums shrink-0",
                  isActive ? "text-foreground" : "text-muted-foreground/60"
                )}>
                  {s.number}
                </span>
                <span>{s.title}</span>
              </a>
            );
          })}
        </nav>

        <div className="mt-auto pt-10" data-testid="sidebar-exec-summary">
          <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground/80">
            Executive Brief
          </div>
          <dl className="mt-4 flex flex-col gap-4 border-t border-border pt-4">
            <div>
              <dt className="font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground/70">
                Purpose
              </dt>
              <dd className="mt-1 text-xs leading-relaxed text-foreground">
                Growth Partner Strategy
              </dd>
            </div>
            <div>
              <dt className="font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground/70">
                Audience
              </dt>
              <dd className="mt-1 text-xs leading-relaxed text-foreground">
                Marketing, Brand &amp; Community Leaders
              </dd>
            </div>
            <div>
              <dt className="font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground/70">
                Read Time
              </dt>
              <dd className="mt-1 text-xs leading-relaxed text-foreground">
                ~10–12 minutes
              </dd>
            </div>
            <div>
              <dt className="font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground/70">
                Sections
              </dt>
              <dd className="mt-1 text-xs leading-relaxed text-foreground">
                15
              </dd>
            </div>
          </dl>
          <a
            href="/HAEVN-Growth-Partner-Executive-Brief.pdf"
            download
            data-testid="sidebar-download-pdf"
            className="mt-6 inline-flex items-center gap-2 border-t border-border pt-4 text-[11px] font-medium tracking-tight text-foreground transition-colors hover:text-foreground/70"
          >
            <span className="inline-block h-1 w-1 rounded-full bg-foreground/60" aria-hidden="true" />
            Download PDF
          </a>
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;
